import {Injectable, inject} from '@angular/core';
import {
  ArchitecturalPrinciple,
  ArchitecturalPrincipleDirectus,
  DirectusService,
  HrTool,
  isSuitePrinciple,
  parseArchitecturalPrinciple,
} from '@frontend/directus';
import {
  ArchitecturalPrincipleControllerService,
  HrToolsControllerService,
  ProjectCoverageDataControllerService,
} from '@frontend/shared-api';
import {firstValueFrom} from 'rxjs';
import {RfiFilterContext, RfiInvitableToolsResult} from './step-5-3-rfi-invitation-list.types';

interface RfiCatalogData {
  registeredHrToolIds: string[];
  registeredHrTools: HrTool[];
  architecturalPrinciples: ArchitecturalPrincipleDirectus[];
}

@Injectable({
  providedIn: 'root',
})
export class RfiInvitationListFilterService {
  private readonly directusService = inject(DirectusService);
  private readonly hrToolsService = inject(HrToolsControllerService);
  private readonly coverageDataService = inject(ProjectCoverageDataControllerService);
  private readonly architecturalPrincipleController = inject(
    ArchitecturalPrincipleControllerService,
  );

  private readonly catalogDataPromise = this.initCatalogData();

  private async initCatalogData(): Promise<RfiCatalogData> {
    // this can be replaced by join this and next call loadHrToolsByIds in BE and then filter the IDs in FE
    // Only tools registered in the backend can be invited, so their ids are needed first.
    const registeredHrToolIds = await firstValueFrom(this.hrToolsService.getRegisteredHrTools());

    // Catalog data of the registered tools, without any filtering.
    const registeredHrTools = await firstValueFrom(
      this.directusService.loadHrToolsByIds(registeredHrToolIds),
    );
    const architecturalPrinciples = await firstValueFrom(
      this.directusService.loadArchitecturalPrinciples(),
    );

    return {
      registeredHrToolIds,
      registeredHrTools,
      architecturalPrinciples,
    };
  }

  async loadInvitableTools(projectId: number): Promise<RfiInvitableToolsResult> {
    const catalogData = await this.catalogDataPromise;

    // What the project requires: module function ids and country codes.
    const coverageData = await firstValueFrom(
      this.coverageDataService.getDataForCalculation({projectId: projectId}),
    );

    // The principle the customer picked for this project, its name holds the Directus id.
    const projectArchitecturalPrinciples = await firstValueFrom(
      this.architecturalPrincipleController.getByProject({projectId: projectId}),
    );
    const selectedArchitecturalPrinciple = this.findSelectedArchitecturalPrinciple(
      catalogData.architecturalPrinciples,
      projectArchitecturalPrinciples[0].name,
    );

    const filterContext = await this.buildFilterContext(
      catalogData.registeredHrToolIds,
      coverageData.officeFunctionsDirectusIds,
      coverageData.countryIsoCodes,
      selectedArchitecturalPrinciple,
    );

    // Keeps the tools that fit the architectural principle and cover a required function and country.
    const invitableTools = catalogData.registeredHrTools.filter((tool) =>
      this.isInvitableTool(tool, filterContext),
    );

    return {
      invitableTools: invitableTools,
      coverageData: coverageData,
    };
  }

  private async buildFilterContext(
    hrToolIds: string[],
    officeFunctionsDirectusIds: string[],
    countryIsoCodes: string[],
    selectedArchitecturalPrinciple: ArchitecturalPrincipleDirectus,
  ): Promise<RfiFilterContext> {
    const requiredModuleFunctionIds = this.parseModuleFunctionIds(officeFunctionsDirectusIds);
    const requiredCountryIsoCodes = new Set(countryIsoCodes ?? []);
    const allowedPrinciples = this.getAllowedPrinciples(selectedArchitecturalPrinciple);

    const composableModuleFunctionIdsByToolId = this.requiresComposableModulesLookup(
      allowedPrinciples,
      requiredModuleFunctionIds,
    )
      ? await firstValueFrom(
          this.directusService.loadComposableModuleFunctionIdsByHrToolIds(hrToolIds),
        )
      : new Map<string, number[]>();

    return {
      requiredModuleFunctionIds: requiredModuleFunctionIds,
      requiredCountryIsoCodes: requiredCountryIsoCodes,
      allowedPrinciples: allowedPrinciples,
      composableModuleFunctionIdsByToolId: composableModuleFunctionIdsByToolId,
    };
  }

  private parseModuleFunctionIds(officeFunctionsDirectusIds: string[]): Set<number> {
    return new Set(
      (officeFunctionsDirectusIds ?? [])
        .map((id) => Number.parseInt(id, 10))
        .filter((id) => !Number.isNaN(id)),
    );
  }

  private findSelectedArchitecturalPrinciple(
    architecturalPrinciples: ArchitecturalPrincipleDirectus[],
    selectedArchitecturalPrincipleId: string,
  ): ArchitecturalPrincipleDirectus {
    // at this moment the list can never be null
    return architecturalPrinciples.find(
      (principle) => principle.id === selectedArchitecturalPrincipleId,
    )!;
  }

  private getAllowedPrinciples(
    selectedArchitecturalPrinciple: ArchitecturalPrincipleDirectus,
  ): Set<ArchitecturalPrinciple> {
    return new Set(selectedArchitecturalPrinciple.principles);
  }

  private requiresComposableModulesLookup(
    allowedPrinciples: Set<ArchitecturalPrinciple>,
    requiredModuleFunctionIds: Set<number>,
  ): boolean {
    if (requiredModuleFunctionIds.size === 0) {
      return false;
    }

    return (
      allowedPrinciples.has(ArchitecturalPrinciple.STANDALONE_SYSTEM) &&
      (!allowedPrinciples.has(ArchitecturalPrinciple.SUITE_WITHOUT_PAYROLL) ||
        !allowedPrinciples.has(ArchitecturalPrinciple.SUITE_WITH_PAYROLL))
    );
  }

  private isInvitableTool(tool: HrTool, filterContext: RfiFilterContext): boolean {
    if (!this.hasMatchingRequiredCountries(tool, filterContext.requiredCountryIsoCodes)) {
      return false;
    }

    const toolPrinciple = parseArchitecturalPrinciple(tool.architecturalPrinciple);
    if (!toolPrinciple) {
      return false;
    }

    if (filterContext.allowedPrinciples.has(toolPrinciple)) {
      return this.hasMatchingRequiredFunctions(tool, filterContext.requiredModuleFunctionIds);
    }

    if (
      this.isSuiteUnderStandaloneProject(toolPrinciple, filterContext) &&
      this.hasMatchingComposableFunctions(tool.id, filterContext)
    ) {
      return true;
    }

    return false;
  }

  private hasMatchingRequiredCountries(
    tool: HrTool,
    requiredCountryIsoCodes: Set<string>,
  ): boolean {
    if (requiredCountryIsoCodes.size === 0) {
      return true;
    }

    return tool.availableCountries.some((country) => requiredCountryIsoCodes.has(country.isoCode));
  }

  private hasMatchingRequiredFunctions(
    tool: HrTool,
    requiredModuleFunctionIds: Set<number>,
  ): boolean {
    if (requiredModuleFunctionIds.size === 0) {
      return true;
    }

    return tool.moduleFunctions.some((moduleFunction) =>
      requiredModuleFunctionIds.has(moduleFunction.id),
    );
  }

  private isSuiteUnderStandaloneProject(
    toolPrinciple: ArchitecturalPrinciple,
    filterContext: RfiFilterContext,
  ): boolean {
    return (
      isSuitePrinciple(toolPrinciple) &&
      filterContext.allowedPrinciples.has(ArchitecturalPrinciple.STANDALONE_SYSTEM)
    );
  }

  private hasMatchingComposableFunctions(toolId: string, filterContext: RfiFilterContext): boolean {
    if (filterContext.requiredModuleFunctionIds.size === 0) {
      return false;
    }

    const composableModuleFunctionIds =
      filterContext.composableModuleFunctionIdsByToolId.get(toolId);
    if (!composableModuleFunctionIds || composableModuleFunctionIds.length === 0) {
      return false;
    }

    return composableModuleFunctionIds.some((moduleFunctionId) =>
      filterContext.requiredModuleFunctionIds.has(moduleFunctionId),
    );
  }
}
